import { useMemo } from 'react';
import { UiVaultConfig, VaultStats } from 'src/@types/vaults';
import { UiVaults } from 'src/constants/vaults';
import { constructVaultStats } from 'src/hooks/vaults/vaultStatsSyncUtils';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { getVaultConfigCached } from 'src/utils/vault/configCache';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useVaultApyReturns } from '../base/useVaultApyReturns';
import { useVaultOnChainData } from '../base/useVaultOnChainData';
import { useExternalVaultStats } from '../base/useExternalVaultStats';

export interface VaultStatWithConfig extends VaultStats {
	uiVaultConfig: UiVaultConfig;
}

const EMPTY_LIST: VaultStatWithConfig[] = [];
const EMPTY_LOOKUP: Record<string, VaultStatWithConfig> = {};

export function useVaultStats(vaultPubkey: string) {
	const { data: apyReturns, isLoading: isApyLoading } = useVaultApyReturns();
	const { data: onChainData, isLoading: isOnChainLoading } =
		useVaultOnChainData();
	const { data: externalStats, isLoading: isExternalLoading } =
		useExternalVaultStats();
	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	const uiVaultConfig = getVaultConfigCached(vaultPubkey);
	const isExternalVault = uiVaultConfig
		? !uiVaultConfig.isVelocityVaultsProgramVault
		: false;

	const vaultStats = useMemo(() => {
		if (isExternalVault) {
			return externalStats?.[vaultPubkey];
		}

		const vaultOnChainData = onChainData?.[vaultPubkey];
		const apyReturnStat = apyReturns?.[vaultPubkey];

		if (!vaultOnChainData) {
			return undefined;
		}

		return constructVaultStats(
			vaultPubkey,
			vaultOnChainData,
			apyReturnStat,
			getOraclePriceForMarket
		);
	}, [
		vaultPubkey,
		isExternalVault,
		externalStats,
		onChainData,
		apyReturns,
		getOraclePriceForMarket,
	]);

	return {
		data: vaultStats,
		isLoading: isExternalVault
			? isExternalLoading
			: isApyLoading || isOnChainLoading,
	};
}

export function useAllVaultStats(filters?: { age?: 'new' | 'old' | 'all' }) {
	const { data: apyReturns, isLoading: isApyLoading } = useVaultApyReturns();
	const { data: onChainData, isLoading: isOnChainLoading } =
		useVaultOnChainData();
	const { data: externalStats, isLoading: isExternalLoading } =
		useExternalVaultStats();
	const areVaultConfigsLoaded = useVaultsStore((s) => s.areVaultConfigsLoaded);
	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	const { list: vaultStatsList, lookup } = useMemo(() => {
		if (!areVaultConfigsLoaded) {
			return { list: EMPTY_LIST, lookup: EMPTY_LOOKUP };
		}

		const list: VaultStatWithConfig[] = [];
		const vaultStatsLookup: Record<string, VaultStatWithConfig> = {};

		const addVaultStat = (vaultPubkey: string, stat: VaultStatWithConfig) => {
			if (stat.uiVaultConfig?.hidden) return;

			if (filters?.age === 'new') {
				if (!stat.uiVaultConfig?.isNew(stat.numOfVaultSnapshots ?? 0)) return;
			} else if (filters?.age === 'old') {
				if (stat.uiVaultConfig?.isNew(stat.numOfVaultSnapshots ?? 0)) return;
			}

			list.push(stat);
			vaultStatsLookup[vaultPubkey] = stat;
		};

		const nativeVaults = UiVaults.getNativeVaults();
		for (const vault of nativeVaults) {
			const vaultPubkey = vault.vaultPubkeyString;
			const vaultOnChainData = onChainData?.[vaultPubkey];
			const apyReturnStat = apyReturns?.[vaultPubkey];

			if (!vaultOnChainData) continue;

			const vaultStats = constructVaultStats(
				vaultPubkey,
				vaultOnChainData,
				apyReturnStat,
				getOraclePriceForMarket
			);

			const uiVaultConfig = getVaultConfigCached(vaultPubkey);
			if (uiVaultConfig) {
				addVaultStat(vaultPubkey, { ...vaultStats, uiVaultConfig });
			}
		}

		if (externalStats) {
			for (const [vaultPubkey, stats] of Object.entries(externalStats)) {
				const uiVaultConfig = getVaultConfigCached(vaultPubkey);
				if (uiVaultConfig) {
					addVaultStat(vaultPubkey, { ...stats, uiVaultConfig });
				}
			}
		}

		return { list, lookup: vaultStatsLookup };
	}, [
		areVaultConfigsLoaded,
		onChainData,
		apyReturns,
		externalStats,
		filters,
		getOraclePriceForMarket,
	]);

	return {
		data: { list: vaultStatsList, lookup },
		isLoading: isApyLoading || isOnChainLoading || isExternalLoading,
	};
}
